<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { toast } from '@/ui-plus/sonner';
import { Loader2, Search, ChevronLeft, ChevronRight, UserCog } from 'lucide-vue-next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import ManageCustomerView from '@/orders-manager/order-modal/customer/ManageCustomerView.vue';

const searchApiPath = storeOrdersApiClient.customers.search[':storeId'].$post;
type Customer = ResType<typeof searchApiPath>["data"]["customers"][0];

const props = defineProps<{
    storeId: string;
}>();

const searchQuery = ref('');
const isSearching = ref(false);
const customers = ref<Customer[]>([]);
const errorBanner = ref<string | null>(null);
const hasNextPage = ref(false);
const currentCursor = ref<{ userId: string } | null>(null);
const cursors = ref<Array<{ userId: string } | null>>([null]);
const currentPageIndex = ref(0);
const blockingUserId = ref<string | null>(null);

const searchCustomers = async (cursor?: { userId: string } | null) => {
    isSearching.value = true;
    errorBanner.value = null;
    try {
        const res = await (await storeOrdersApiClient.customers.search[':storeId'].$post({
            param: { storeId: props.storeId },
            json: {
                q: searchQuery.value.trim(),
                cursor: cursor || undefined,
            },
        })).json();
        if (res.error) {
            errorBanner.value = res.error.message || 'Failed to search customers';
            console.error(errorBanner.value);
            toast.error(errorBanner.value);
            return;
        }
        customers.value = res.data.customers || [];
        hasNextPage.value = res.data.hasNextPage;
        currentCursor.value = res.data.nextCursor;
    } catch (e) {
        console.error(e);
        errorBanner.value = 'An error occurred while searching';
        toast.error(errorBanner.value);
    } finally {
        isSearching.value = false;
    }
};

const searchFromStart = async () => {
    currentPageIndex.value = 0;
    cursors.value = [null];
    currentCursor.value = null;
    await searchCustomers(null);
};

const goToNextPage = () => {
    if (hasNextPage.value && currentCursor.value) {
        cursors.value.push(currentCursor.value);
        currentPageIndex.value++;
        searchCustomers(currentCursor.value);
    }
};

const goToPrevPage = () => {
    if (currentPageIndex.value > 0) {
        cursors.value.pop();
        currentPageIndex.value--;
        const prevCursor = cursors.value[cursors.value.length - 1];
        searchCustomers(prevCursor);
    }
};

const setCustomerBlocked = (userId: string, isBlocked: boolean) => {
    customers.value = customers.value.map((customer) =>
        customer.userId === userId ? { ...customer, isBlocked } : customer
    );
};

const toggleBlock = async (customer: Customer) => {
    blockingUserId.value = customer.userId;
    try {
        const api = customer.isBlocked
            ? storeOrdersApiClient.customers.unblock[':storeId'].$post
            : storeOrdersApiClient.customers.block[':storeId'].$post;
        const res = await (await api({
            param: { storeId: props.storeId },
            json: { userId: customer.userId },
        })).json();
        if (res.error) {
            toast.error(res.error.message || 'Failed to update customer block status');
            return;
        }
        setCustomerBlocked(customer.userId, res.data.isBlocked);
        toast.success(res.data.isBlocked ? 'Customer blocked' : 'Customer unblocked');
    } catch (e) {
        console.error(e);
        toast.error('Failed to update customer block status');
    } finally {
        blockingUserId.value = null;
    }
};

onMounted(() => {
    searchFromStart();
});
</script>

<template>
    <div class="space-y-6 mt-4">
        <Card class="max-w-lg mx-auto">
            <CardHeader>
                <CardTitle>Search customer</CardTitle>
                <CardDescription>
                    Search by name, email, or phone.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form class="flex gap-2"
                      @submit.prevent="searchFromStart">
                    <Input v-model="searchQuery"
                           :disabled="isSearching" />
                    <Button type="submit"
                            :disabled="isSearching">
                        <Loader2 v-if="isSearching"
                                 class="h-4 w-4 animate-spin" />
                        <Search v-else
                                class="h-4 w-4" />
                    </Button>
                </form>
            </CardContent>
        </Card>

        <div v-if="errorBanner"
             class="max-w-lg mx-auto bg-destructive/15 text-destructive border border-destructive/30 px-4 py-3 rounded">
            <p>{{ errorBanner }}</p>
        </div>

        <div v-else-if="isSearching && customers.length === 0"
             class="flex justify-center py-8">
            <Loader2 class="h-8 w-8 animate-spin" />
        </div>

        <Card v-else-if="!isSearching && customers.length === 0"
              class="max-w-lg mx-auto">
            <CardContent class="py-8 text-center text-muted-foreground">
                No customer found for this store.
            </CardContent>
        </Card>

        <div v-else
             class="max-w-lg mx-auto space-y-3">
            <Card v-for="customer in customers"
                  :key="customer.userId">
                <CardHeader>
                    <div class="flex items-center justify-between gap-2">
                        <CardTitle>{{ customer.name || 'Unnamed customer' }}</CardTitle>
                        <Badge v-if="customer.isBlocked"
                               variant="destructive">
                            Blocked
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent class="space-y-3">
                    <div class="space-y-1 text-sm">
                        <div v-if="customer.email">{{ customer.email }}</div>
                        <div v-if="customer.phone">{{ customer.phone }}</div>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <Dialog>
                            <DialogTrigger as-child>
                                <Button variant="outline"
                                        size="sm">
                                    <UserCog class="h-4 w-4 mr-1" />
                                    Manage
                                </Button>
                            </DialogTrigger>
                            <DialogScrollContent class="sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Manage customer</DialogTitle>
                                </DialogHeader>
                                <ManageCustomerView :store-id="storeId"
                                                    :user-id="customer.userId" />
                            </DialogScrollContent>
                        </Dialog>

                        <Button @click="toggleBlock(customer)"
                                :variant="customer.isBlocked ? 'outline' : 'destructive'"
                                :disabled="blockingUserId === customer.userId"
                                size="sm">
                            {{ blockingUserId === customer.userId
                                ? 'Updating...'
                                : (customer.isBlocked ? 'Unblock' : 'Block') }}
                        </Button>
                    </div>
                </CardContent>
            </Card>
            <div class="flex items-center justify-between pt-2">
                <div class="text-sm text-muted-foreground">
                    Page {{ currentPageIndex + 1 }}
                </div>
                <div class="flex gap-2">
                    <Button @click="goToPrevPage"
                            :disabled="currentPageIndex === 0 || isSearching"
                            variant="outline"
                            size="sm">
                        <ChevronLeft class="h-4 w-4 mr-1" />
                        Previous
                    </Button>
                    <Button @click="goToNextPage"
                            :disabled="!hasNextPage || isSearching"
                            variant="outline"
                            size="sm">
                        Next
                        <ChevronRight class="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </div>
        </div>


    </div>
</template>
